import { App } from "../ui/App.ts";
import { themeBadgeColors } from "../utils/helpers.ts";
import { t } from "../i18n.ts";

export class GalleryManager {
  private app: App;
  public galleryPosters: any[] = [];
  public currentPosterIndex = 0;
  public currentFilter = "all";

  constructor(app: App) {
    this.app = app;
  }

  async loadGallery() {
    try {
      const response = await fetch("/api/posters");
      const data = await response.json();
      const posters = data.posters || [];
      this.galleryPosters = posters;

      const countKey = posters.length !== 1 ? "gallery.countPlural" : "gallery.count";
      const galleryCount = document.getElementById("gallery-count");
      if (galleryCount) {
        galleryCount.textContent = t(countKey, this.app.currentLang, { count: posters.length });
      }

      this.renderFilterChips(posters);

      const grid = document.getElementById("gallery-grid");
      const empty = document.getElementById("gallery-empty");
      if (grid && empty) {
        if (posters.length === 0) {
          grid.style.display = "none";
          empty.style.display = "block";
        } else {
          grid.style.display = "grid";
          empty.style.display = "none";
          this.renderGallery(this.getFilteredPosters());
        }
      }
    } catch (error) {
      console.error("Failed to load gallery:", error);
    }
  }

  renderFilterChips(posters: any[]) {
    const container = document.getElementById("filter-chips");
    if (!container) return;

    const themes = [...new Set(posters.map((p) => p.theme))];

    container.innerHTML = `
      <div class="filter-chip ${
      this.currentFilter === "all" ? "active" : ""
    } flex h-9 items-center justify-center gap-x-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 px-5" data-filter="all">
        <p class="text-sm font-semibold">${t("gallery.allPosters", this.app.currentLang)}</p>
      </div>
      ${
      themes.map((theme) => `
        <div class="filter-chip ${
        this.currentFilter === theme ? "active" : ""
      } flex h-9 items-center justify-center gap-x-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 px-5" data-filter="${theme}">
          <p class="text-sm font-medium">${theme}</p>
        </div>
      `).join("")
    }
    `;

    container.querySelectorAll(".filter-chip").forEach((chip) => {
      chip.addEventListener("click", () => {
        this.currentFilter = (chip as HTMLElement).dataset.filter || "all";
        container.querySelectorAll(".filter-chip").forEach((c) => c.classList.remove("active"));
        chip.classList.add("active");
        this.renderGallery(this.getFilteredPosters());
      });
    });
  }

  getFilteredPosters() {
    if (this.currentFilter === "all") {
      return this.galleryPosters;
    }
    return this.galleryPosters.filter((p) => p.theme === this.currentFilter);
  }

  getThemeBadgeColor(theme: string) {
    const themeLower = theme.toLowerCase();
    return themeBadgeColors[themeLower] || themeBadgeColors["default"];
  }

  renderGallery(posters: any[]) {
    const grid = document.getElementById("gallery-grid");
    if (!grid) return;

    const posterCards = posters.map((poster) => {
      const badgeColor = this.getThemeBadgeColor(poster.theme);
      return `
      <div class="poster-card bg-white dark:bg-slate-800 rounded-xl p-3 shadow-sm border border-slate-100 dark:border-slate-700 group cursor-pointer"
           onclick="app.galleryManager.viewPoster('${poster.id}')">
        <div class="relative w-full aspect-[2/3] bg-slate-100 dark:bg-slate-900 rounded-lg overflow-hidden mb-4">
          <img src="${poster.imageData}" alt="${poster.city}, ${poster.country}"
               class="w-full h-full object-cover transition-transform group-hover:scale-105"/>
          <div class="absolute top-3 left-3">
            <span class="${badgeColor} backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">${poster.theme}</span>
          </div>
          <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button onclick="event.preventDefault(); event.stopPropagation(); app.posterRenderer.downloadPosterById('${poster.id}', 'png')"
                    class="size-10 rounded-full bg-white text-primary flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                    title="Download PNG">
              <span class="material-symbols-outlined text-xl">image</span>
            </button>
            <button onclick="event.preventDefault(); event.stopPropagation(); app.posterRenderer.downloadPosterById('${poster.id}', 'svg')"
                    class="size-10 rounded-full bg-white text-violet-500 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                    title="Download SVG">
              <span class="material-symbols-outlined text-xl">polyline</span>
            </button>
            <button onclick="event.preventDefault(); event.stopPropagation(); app.galleryManager.deletePoster('${poster.id}')"
                    class="size-10 rounded-full bg-white text-rose-500 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                    title="Delete Poster">
              <span class="material-symbols-outlined text-xl">delete</span>
            </button>
          </div>
        </div>
        <div class="px-1 flex justify-between items-start">
          <div>
            <h3 class="text-slate-900 dark:text-white text-lg font-bold leading-none mb-1">${poster.city.toUpperCase()}</h3>
            <p class="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-widest">${poster.country}</p>
          </div>
          <p class="text-[10px] text-slate-400 dark:text-slate-500 font-medium">${
        new Date(poster.timestamp).toLocaleDateString()
      }</p>
        </div>
      </div>
    `;
    }).join("");

    const themeOption = this.currentFilter !== "all" ? `{ theme: '${this.currentFilter}' }` : "{}";
    const placeholderCard = `
      <div class="group cursor-pointer border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-3 flex flex-col items-center justify-center min-h-[300px] hover:border-primary/50 transition-colors bg-white/50 dark:bg-slate-800/30"
           onclick="app.switchTab('generate', ${themeOption})">
        <div class="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <span class="material-symbols-outlined text-3xl">add</span>
        </div>
        <p class="text-slate-900 dark:text-white font-bold">${
      t("gallery.newPoster", this.app.currentLang)
    }</p>
        <p class="text-slate-500 dark:text-slate-400 text-sm">${
      t("gallery.newPosterDesc", this.app.currentLang)
    }</p>
      </div>
    `;

    grid.innerHTML = posterCards + placeholderCard;
  }

  viewPoster(id: string) {
    const filteredPosters = this.getFilteredPosters();
    const index = filteredPosters.findIndex((p) => p.id === id);
    if (index === -1) return;

    this.currentPosterIndex = index;
    this.showPosterInModal(filteredPosters[index], filteredPosters.length);
  }

  showPosterInModal(poster: any, totalCount: number) {
    if (!poster) return;

    const modalTitle = document.getElementById("modal-title");
    const modalSubtitle = document.getElementById("modal-subtitle");
    const modalImage = document.getElementById("modal-image") as HTMLImageElement;

    if (modalTitle) modalTitle.textContent = `${poster.city}, ${poster.country}`;
    if (modalSubtitle) modalSubtitle.textContent = `${poster.theme} Theme`;
    if (modalImage) modalImage.src = poster.imageData;

    const modalProgress = document.getElementById("modal-progress");
    if (modalProgress) {
      modalProgress.textContent = t("modal.artwork", this.app.currentLang, {
        current: this.currentPosterIndex + 1,
        total: totalCount,
      });
    }

    const dotsContainer = document.getElementById("modal-dots");
    if (dotsContainer) {
      dotsContainer.innerHTML = Array.from({ length: Math.min(totalCount, 15) }, (_, i) => {
        const isActive = i === this.currentPosterIndex;
        return `<div 
          onclick="app.galleryManager.jumpToPoster(${i})"
          class="w-2 h-2 rounded-full cursor-pointer transition-all hover:scale-150 ${
          isActive
            ? "bg-primary"
            : "bg-slate-900/20 dark:bg-white/20 hover:bg-slate-900/40 dark:hover:bg-white/40"
        }"
          title="Artwork ${i + 1}"></div>`;
      }).join("");
    }

    const prevBtn = document.getElementById("modal-prev");
    const nextBtn = document.getElementById("modal-next");
    if (prevBtn) prevBtn.style.visibility = this.currentPosterIndex > 0 ? "visible" : "hidden";
    if (nextBtn) {
      nextBtn.style.visibility = this.currentPosterIndex < totalCount - 1 ? "visible" : "hidden";
    }

    const modalDownload = document.getElementById("modal-download");
    if (modalDownload) {
      modalDownload.onclick = (e) => {
        e.preventDefault();
        // Since we refactored, we can route it through PosterRenderer
        (this.app as any).posterRenderer.downloadPosterById(poster.id, "png");
      };
    }

    const modalDownloadSvg = document.getElementById("modal-download-svg");
    if (modalDownloadSvg) {
      modalDownloadSvg.onclick = (e) => {
        e.preventDefault();
        (this.app as any).posterRenderer.downloadPosterById(poster.id, "svg");
      };
    }

    const modalDelete = document.getElementById("modal-delete");
    if (modalDelete) {
      modalDelete.onclick = async (e) => {
        e.preventDefault();
        await this.deletePoster(poster.id);
        this.app.closeModal();
      };
    }

    const modal = document.getElementById("poster-modal");
    if (modal) {
      modal.classList.remove("hidden");
      modal.classList.add("flex");
    }
  }

  navigatePoster(direction: number) {
    const filteredPosters = this.getFilteredPosters();
    const newIndex = this.currentPosterIndex + direction;

    if (newIndex >= 0 && newIndex < filteredPosters.length) {
      this.currentPosterIndex = newIndex;
      this.showPosterInModal(filteredPosters[newIndex], filteredPosters.length);
    }
  }

  jumpToPoster(index: number) {
    const filteredPosters = this.getFilteredPosters();
    if (index >= 0 && index < filteredPosters.length) {
      this.currentPosterIndex = index;
      this.showPosterInModal(filteredPosters[index], filteredPosters.length);
    }
  }

  async deletePoster(id: string) {
    const confirmed = await this.app.showConfirm("Are you sure you want to delete this poster?");
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`/api/posters/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        await this.loadGallery();
      }
    } catch (error) {
      console.error("Failed to delete poster:", error);
    }
  }

  async savePoster(city: string, country: string, lat: number, lon: number, distance: number) {
    try {
      if (!this.app.canvas) return;

      const imageData = this.app.canvas.toDataURL("image/png");
      const theme = this.app.themeManager.currentTheme?.name || "default";

      const response = await fetch("/api/posters", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          city,
          country,
          theme,
          imageData,
          lat,
          lon,
          distance,
        }),
      });

      if (response.ok) {
        const poster = await response.json();
        this.app.currentPosterId = poster.id;
        console.log("Poster saved:", poster.id);
      }
    } catch (error) {
      console.error("Failed to save poster:", error);
    }
  }
}
